import type { ResultTier } from './result-badge';

/**
 * Procedural audio for the timing games (owner decision, 2026-08-18: "we need
 * vibes!!! movement, a bit of sound but nothing timable"). Synthesized with
 * Web Audio oscillators, no sample assets — matching stack-tower-audio.ts.
 *
 * The hard constraint is **nothing timable**: a timing game must never emit a
 * sound the player could count against to hit the target, or the whole game
 * breaks. So the hold sound is a single steady drone with only a short
 * one-shot pitch settle at the very start (a "winding up, then time slows"
 * feel) and then a flat, rhythm-free pad — there is deliberately no tick,
 * pulse, beat, or ongoing sweep that repeats on any interval. Only the
 * RELEASE sound carries information, and only after the attempt is already
 * over, coloured by how close it landed.
 *
 * One shared, lazily-created `AudioContext`: the press that starts a hold is
 * itself the user gesture that unlocks it (browsers refuse audio before one).
 */

let ctx: AudioContext | null = null;
const MUTE_KEY = 'rw-timing-muted';
let muted = readMutedPreference();

function readMutedPreference(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

function getContext(): AudioContext | null {
  if (typeof window === 'undefined' || typeof AudioContext === 'undefined') return null;
  if (!ctx) ctx = new AudioContext();
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

export function isTimingSoundMuted(): boolean {
  return muted;
}

export function setTimingSoundMuted(next: boolean): void {
  muted = next;
  try {
    localStorage.setItem(MUTE_KEY, next ? '1' : '0');
  } catch {
    /* private mode; the in-memory flag still applies for this session */
  }
  if (muted) stopHoldSound();
}

// ---- the hold drone ----------------------------------------------------

interface Drone {
  readonly oscillators: OscillatorNode[];
  readonly gain: GainNode;
}

let drone: Drone | null = null;

/**
 * Starts the steady hold drone: two slightly detuned low oscillators through a
 * gentle low-pass, at a low, non-startling level. A single short downward
 * pitch settle at the start reads as the stopwatch "spinning up then time
 * dilating"; after that it holds flat — no repeating motion the ear could use
 * to keep time.
 */
export function startHoldSound(): void {
  if (muted) return;
  const audio = getContext();
  if (!audio) return;
  stopHoldSound();

  const now = audio.currentTime;
  const gain = audio.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.06, now + 0.12);

  const filter = audio.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(650, now);

  const base = 120;
  const oscillators: OscillatorNode[] = [];
  for (const [freq, detune, type] of [
    [base, 0, 'triangle'],
    [base, 7, 'sine'],
  ] as const) {
    const osc = audio.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(freq * 1.12, now);
    // one-shot glide down to the resting pitch over 0.5s, then dead steady
    osc.frequency.exponentialRampToValueAtTime(freq, now + 0.5);
    osc.detune.setValueAtTime(detune, now);
    osc.connect(filter);
    osc.start(now);
    oscillators.push(osc);
  }
  filter.connect(gain);
  gain.connect(audio.destination);
  drone = { oscillators, gain };
}

/** Fades the hold drone out fast and tears its nodes down. */
export function stopHoldSound(): void {
  if (!drone || !ctx) {
    drone = null;
    return;
  }
  const { oscillators, gain } = drone;
  drone = null;
  const now = ctx.currentTime;
  try {
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
    oscillators.forEach((osc) => osc.stop(now + 0.16));
  } catch {
    /* nodes already stopped */
  }
}

// ---- the release sting -------------------------------------------------

interface ToneSpec {
  readonly frequency: number;
  readonly duration: number;
  readonly type?: OscillatorType;
  readonly peakGain?: number;
}

function playTone({ frequency, duration, type = 'sine', peakGain = 0.14 }: ToneSpec, startOffsetSec = 0): void {
  const audio = getContext();
  if (!audio) return;
  const startAt = audio.currentTime + startOffsetSec;
  const osc = audio.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(frequency, startAt);
  const gain = audio.createGain();
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(peakGain, startAt + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
  osc.connect(gain);
  gain.connect(audio.destination);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.03);
}

/**
 * The release sting, coloured by how close the attempt landed. Bright, rising
 * and layered for a good hit; low, short and dull for a bad one — so the ear
 * learns the same green→red language the colours already speak. This fires
 * only AFTER the player has released, so it can never be used to time.
 */
export function playReleaseSound(tier: ResultTier): void {
  if (muted) return;
  const audio = getContext();
  if (!audio) return;

  switch (tier) {
    case 'gold':
      // a bright, ringing major arpeggio — the jackpot
      playTone({ frequency: 784, duration: 0.16, type: 'triangle', peakGain: 0.16 });
      playTone({ frequency: 988, duration: 0.18, type: 'triangle', peakGain: 0.14 }, 0.06);
      playTone({ frequency: 1319, duration: 0.28, type: 'sine', peakGain: 0.12 }, 0.12);
      break;
    case 'green':
      playTone({ frequency: 660, duration: 0.14, type: 'triangle', peakGain: 0.15 });
      playTone({ frequency: 880, duration: 0.16, type: 'triangle', peakGain: 0.1 }, 0.05);
      break;
    case 'yellow':
      playTone({ frequency: 523, duration: 0.13, type: 'sine', peakGain: 0.13 });
      break;
    case 'orange':
      playTone({ frequency: 392, duration: 0.14, type: 'sine', peakGain: 0.13 });
      break;
    case 'red':
      playTone({ frequency: 300, duration: 0.16, type: 'sawtooth', peakGain: 0.11 });
      playTone({ frequency: 220, duration: 0.2, type: 'sawtooth', peakGain: 0.1 }, 0.07);
      break;
    case 'way-off':
      // a dull descending thud — clearly the worst outcome
      playTone({ frequency: 180, duration: 0.22, type: 'square', peakGain: 0.11 });
      playTone({ frequency: 120, duration: 0.26, type: 'square', peakGain: 0.1 }, 0.08);
      break;
    case 'false-start':
      // a sharp dissonant error buzz
      playTone({ frequency: 200, duration: 0.2, type: 'sawtooth', peakGain: 0.12 });
      playTone({ frequency: 213, duration: 0.2, type: 'sawtooth', peakGain: 0.1 });
      break;
  }
}
